import { Children, ComponentType, ReactElement, ReactNode } from 'react';

import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

import { Avatar, AvatarColor } from '@components/avatar';
import { Badge } from '@components/badge';
import { Card } from '@components/card';
import { IconButton } from '@components/icon-button';
import type { IconSmallProps } from '@components/icons';
import { IconFormIndicatorError } from '@components/icons/form-indicator-error';
import { IconOnboarding } from '@components/icons/onboarding';
import { IconStar } from '@components/icons/star';
import { IconStarSolid } from '@components/icons/star-solid';
import { Stack } from '@components/stack';
import { getStatusLineColor, StatusLineProps } from '@components/status-line';
import { Text } from '@components/text';
import { Tooltip } from '@components/tooltip';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

export type ApplicantCardProps = {
  profile: ReactElement<ProfileProps>;
  active?: boolean;
  activity?: ReactNode;
  tags?: ReactNode;
  statusLine?: StatusLineProps['variant'];
  onClick?(): void;
  onOnboardingClick?(): void;
  onFavoriteClick?(): void;
  isFavorited?: boolean;

  // When true, show no hover effects.
  flat?: boolean;
};

const ICON_SIZE = 16;

export const ApplicantCard = observer<ApplicantCardProps>(function ApplicantCard(props) {
  const { t } = useScopedTranslate(locale);
  const {
    activity,
    profile,
    statusLine,
    onClick,
    onOnboardingClick,
    onFavoriteClick,
    isFavorited = false,
    tags,
    active = false,
    flat = false,
  } = props;

  return (
    <ApplicantCardContainer
      $active={active}
      $flat={flat}
      $statusLine={statusLine}
      onClick={onClick}
      space={3}
    >
      <Content>
        <Stack orientation="horizontal" justify="space-between">
          {profile}
          <Stack orientation="horizontal">
            {onOnboardingClick && (
              <OnboardingLink
                variant="ghost-purple"
                size="sm"
                icon={<IconOnboarding size={ICON_SIZE} />}
                stopPropagation
                preventDefault
                onClick={onOnboardingClick}
              />
            )}
            {onFavoriteClick && (
              <IconButton
                tooltip={isFavorited ? t('removeFromFavorites') : t('addToFavorites')}
                variant="ghost-purple"
                size="sm"
                icon={
                  isFavorited ? (
                    <IconStarSolid color="orange100" size={ICON_SIZE} />
                  ) : (
                    <IconStar size={ICON_SIZE} />
                  )
                }
                stopPropagation
                preventDefault
                onClick={onFavoriteClick}
              />
            )}
          </Stack>
        </Stack>
        {activity}
        {tags}
      </Content>
    </ApplicantCardContainer>
  );
});

const ACTIVE_RING = `inset 0 0 0 ${pxToRem(2)} ${theme.color.elementBlue100}`;

const ApplicantCardContainer = styled(Card)<{
  $active: boolean;
  $flat: boolean;
  $statusLine?: StatusLineProps['variant'];
}>((p) => ({
  // Clipped by the card rather than given its own radius — at 4px wide it
  // cannot reproduce the corner curve.
  ...(p.$statusLine && {
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 0,
      bottom: 0,
      width: pxToRem(4),
      backgroundColor: getStatusLineColor(p.$statusLine),
    },
  }),

  // A ring rather than a thicker border, so selecting a card neither resizes it
  // nor competes with the status bar for the left edge.
  ...(p.$active && {
    boxShadow: ACTIVE_RING,
  }),

  ...(!p.$flat &&
    !p.$active && {
      ':hover': {
        boxShadow: theme.shadow.low,
      },
    }),
}));

const Content = styled(Stack).attrs({
  space: 2,
  orientation: 'vertical',
})({
  minWidth: 0,
  flex: 1,
});

const OnboardingLink = styled(IconButton)({
  width: '2rem',
  height: '2rem',
});

type ProfileProps = {
  name: string;
  avatar?: string;
  detail?: string;
  avatarBackground?: AvatarColor;
};

export const ApplicantCardProfile = observer<ProfileProps>(function Profile(props) {
  const { name, avatar, detail, avatarBackground } = props;

  return (
    <ProfileContainer>
      <Avatar name={name} src={avatar} size="lg" color={avatarBackground} />
      <UserInfo>
        <Text ellipsis font="bold14" color="purple100" text={name} />
        {detail && <Text ellipsis font="regular12" color="purple60" text={detail} />}
      </UserInfo>
    </ProfileContainer>
  );
});

type CardActivityChild = ReactElement<CardActivityItemProps | null> | boolean;
type CardActivityProps = {
  children: CardActivityChild | Array<CardActivityChild>;
};

export const ApplicantCardActivity = observer<CardActivityProps>(function CardActivity(props) {
  const children = Children.toArray(props.children).filter(Boolean);
  if (!children.length) return null;

  return (
    <Stack orientation="horizontal" space={1} wrap>
      {children}
    </Stack>
  );
});

type CardActivityItemProps = {
  value: string | number;
  highlight?: boolean;
  hasError?: boolean;
  icon?: ComponentType<IconSmallProps>;
  tooltip?: string;
  onClick?(): void;
};

export const ApplicantCardActivityItem = observer<CardActivityItemProps>(
  function ApplicantCardActivityItem(props) {
    const { icon: Icon, value, highlight = false, hasError = false, tooltip, onClick } = props;

    const content = (
      <ActivityContainer>
        {Icon && (
          <ActivityIconContainer
            $clickable={Boolean(onClick)}
            onClick={(e) => {
              if (onClick) {
                e.preventDefault();
                e.stopPropagation();
                onClick();
              }
            }}
          >
            <Icon color="purple60" />
          </ActivityIconContainer>
        )}
        {hasError && <IconFormIndicatorError size={16} />}
        <Badge variant={highlight ? 'green' : 'neutral'} text={value} />
      </ActivityContainer>
    );

    if (tooltip) {
      return <Tooltip content={tooltip}>{content}</Tooltip>;
    }
    return content;
  }
);

type CardTagsProps = {
  children: ReactNode;
};
export const ApplicantCardTags = observer<CardTagsProps>(function CardTags(props) {
  const children = Children.toArray(props.children).filter(Boolean);
  if (!children.length) return null;

  return (
    <Stack orientation="horizontal" space={1} wrap>
      {children}
    </Stack>
  );
});

const ActivityContainer = styled(Stack).attrs({
  space: 1,
  orientation: 'horizontal',
  align: 'center',
})({
  display: 'inline-flex',
});

const ActivityIconContainer = styled.div<{ $clickable: boolean }>((p) => ({
  width: pxToRem(16),
  height: pxToRem(16),
  ...(p.$clickable && {
    '&:hover svg': {
      cursor: 'pointer',
      color: theme.color.elementBlue100,
    },
  }),
}));

const ProfileContainer = styled(Stack).attrs({
  space: 2,
  orientation: 'horizontal',
  align: 'center',
})({
  minWidth: 0,
  flexGrow: 1,
});

const UserInfo = styled.div({
  overflow: 'hidden',
});

const locale = {
  en: {
    addToFavorites: 'Add to favourites',
    removeFromFavorites: 'Remove from favourites',
  },
  is: {
    addToFavorites: 'Bæta við uppáhalds',
    removeFromFavorites: 'Fjarlægja úr uppáhalds',
  },
};
